import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Armchair, Clock, ScanSearch, TrendingDown, Percent, MapPin, Power, Trash2, SlidersHorizontal, Save, Heart } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";

const RANGES = [
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "quarter", label: "Quarter" },
  { key: "year", label: "Year" },
  { key: "pending", label: "Pending" },
];

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-[#2A3550]/60 bg-[#1E2638] px-4 py-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-1 font-display text-2xl font-light" style={accent ? { color: accent } : {}}>{value}</div>
    </div>
  );
}

export default function SeatHistoryModal({ storeId, chair, open, onOpenChange, onChanged, onRemoved }) {
  const { money, branding } = useBranding();
  const [range, setRange] = useState("month");
  const [active, setActive] = useState(true);
  const [ov, setOv] = useState({ stage1_min: "", sale_engage_min: "", expected_min: "" });
  const [busy, setBusy] = useState(false);
  const [charitable, setCharitable] = useState(false);
  const [benId, setBenId] = useState("");
  const [newBen, setNewBen] = useState("");
  const qc = useQueryClient();

  useEffect(() => { if (open) setRange("month"); }, [open, chair?.id]);
  useEffect(() => {
    if (open && chair) {
      setActive(chair.active !== false);
      setCharitable(!!chair.charitable);
      setBenId(chair.beneficiary_id || "");
      const t = chair.thresholds || {};
      setOv({ stage1_min: t.stage1_min ?? "", sale_engage_min: t.sale_engage_min ?? "", expected_min: t.expected_min ?? "" });
    }
  }, [open, chair?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: gthr } = useQuery({ queryKey: ["thresholds"], queryFn: () => api.get("/thresholds").then((r) => r.data), enabled: open });
  const { data: beneficiaries } = useQuery({ queryKey: ["beneficiaries"], queryFn: () => api.get("/beneficiaries").then((r) => r.data), enabled: open });

  const patch = async (payload, msg) => {
    if (!storeId || !chair) return;
    setBusy(true);
    try {
      await api.patch(`/stores/${storeId}/chairs/${chair.id}`, payload);
      toast.success(msg);
      onChanged && onChanged();
    } catch { toast.error("Could not update this chair — check your access"); }
    setBusy(false);
  };

  const toggleActive = async () => {
    const next = !active;
    setActive(next);
    await patch({ active: next }, next ? "Chair brought back online" : "Chair taken offline");
  };

  const saveThresholds = () => {
    const t = {};
    ["stage1_min", "sale_engage_min", "expected_min"].forEach((k) => { if (ov[k] !== "" && ov[k] != null) t[k] = Number(ov[k]); });
    if (Object.keys(t).length === 0) return patch({ clear_thresholds: true }, "Reverted to store default thresholds");
    patch({ thresholds: t }, "Custom thresholds saved for this chair");
  };

  const useDefaults = () => { setOv({ stage1_min: "", sale_engage_min: "", expected_min: "" }); patch({ clear_thresholds: true }, "Reverted to store default thresholds"); };

  const saveCharitable = (nextOn, nextBenId) =>
    patch({ charitable: nextOn, beneficiary_id: nextBenId || null },
      nextOn ? "Charitable chair updated" : "Charitable giving turned off");
  const toggleCharitable = () => { const n = !charitable; setCharitable(n); saveCharitable(n, benId); };
  const addBeneficiary = async () => {
    const name = newBen.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/beneficiaries", { name });
      setNewBen("");
      setBenId(data.id);
      qc.invalidateQueries({ queryKey: ["beneficiaries"] });
      saveCharitable(true, data.id);
    } catch { toast.error("Could not add this cause"); }
  };

  const removeChair = async () => {
    if (!storeId || !chair) return;
    setBusy(true);
    try {
      await api.delete(`/stores/${storeId}/chairs/${chair.id}`);
      toast.success("Chair removed — its history is kept");
      onRemoved && onRemoved();
    } catch { toast.error("Could not remove this chair"); }
    setBusy(false);
  };

  const { data } = useQuery({
    queryKey: ["seat-history", storeId, chair?.id, range],
    queryFn: () => api.get(`/stores/${storeId}/chairs/${chair.id}/history`, { params: { range } }).then((r) => r.data),
    enabled: !!(open && storeId && chair?.id),
  });

  if (!chair) return null;
  const s = data?.summary;
  const c = data?.classification || { quick: 0, standard: 0, extended: 0 };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="block w-[calc(100vw-1.5rem)] sm:w-full max-w-3xl p-0 gap-0 bg-[#1E2638] border-[#2A3550]/70 overflow-hidden" data-testid="seat-history-modal">
        <DialogTitle className="sr-only">Seat history — {chair.label}</DialogTitle>

        {/* Header */}
        <div className="px-4 sm:px-7 pt-6 pb-5 border-b border-[#232C40] bg-[#1E2638]">
          <div className="flex items-center gap-3 pr-8">
            <div className="h-11 w-11 rounded-xl flex items-center justify-center border border-[#2A3550]/70 shrink-0" style={{ backgroundColor: "#232C40" }}>
              <Armchair className="h-5 w-5" style={{ color: branding.primary_color }} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Seat history</div>
              <h2 className="font-display text-lg sm:text-xl font-medium tracking-tight truncate">
                {chair.label} · Store #{data?.store?.number ?? ""}
              </h2>
              <div className="text-xs text-slate-400 flex items-center gap-1.5 mt-0.5 truncate">
                <MapPin className="h-3 w-3 shrink-0" />
                <span className="truncate">
                  {data?.store?.name}
                  {typeof data?.chair?.position === "number" && ` · Floor position ${data.chair.position + 1}`}
                </span>
              </div>
            </div>
          </div>
          <div className="mt-4 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <Tabs value={range} onValueChange={setRange}>
              <TabsList className="bg-[#171E2E] border border-[#2A3550]/60 w-max">
                {RANGES.map((r) => (
                  <TabsTrigger key={r.key} value={r.key} data-testid={`seat-range-${r.key}`}>{r.label}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        </div>

        <div className="px-4 sm:px-7 py-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="seat-history-kpis">
            <Stat icon={ScanSearch} label={range === "pending" ? "Pending" : "Services"} value={s?.services ?? "—"} />
            <Stat icon={Percent} label="Match rate" value={s ? `${s.match_rate}%` : "—"} accent={branding.accent_color} />
            <Stat icon={TrendingDown} label="Unaccounted" value={s ? money(s.unaccounted_revenue) : "—"} accent={branding.primary_color} />
            <Stat icon={Clock} label="Avg duration" value={s ? `${s.avg_duration}m` : "—"} />
          </div>

          {range === "pending" && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-300 text-sm px-4 py-3" data-testid="pending-note">
              Pending flags stay open until midnight — giving staff time to ring a cash payment into the till before anything escalates to the owner.
            </div>
          )}

          {/* Trend chart */}
          <div className="rounded-xl border border-[#2A3550]/50 bg-[#1E2638] p-4">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
              <div className="text-sm font-medium">Services vs. unmatched flags</div>
              <div className="flex items-center gap-4 text-xs text-slate-400 shrink-0">
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: "#64748B" }} /> Services</span>
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: branding.primary_color }} /> Flagged</span>
              </div>
            </div>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%" minHeight={180}>
                <BarChart data={data?.series || []} barCategoryGap="22%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#2A3550" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#3B82F6" }} interval="preserveStartEnd" axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#1E2638", border: "1px solid #2A3550", borderRadius: 10, fontSize: 12 }} cursor={{ fill: "rgba(198,160,74,0.08)" }} />
                  <Bar dataKey="services" fill="#64748B" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="flagged" fill={branding.primary_color} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Classification + recent */}
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold mb-3">Service mix</div>
              <div className="space-y-2">
                {[["Quick", c.quick, "#3B82F6"], ["Standard", c.standard, "#06B6D4"], ["Extended", c.extended, "#EC4899"]].map(([name, val, color]) => (
                  <div key={name} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-slate-300"><span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} /> {name}</span>
                    <span className="text-slate-400">{val}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold mb-3">Recent at this seat</div>
              <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                {(data?.recent || []).map((ev) => (
                  <div key={ev.id} className="flex items-center justify-between text-sm rounded-lg border border-[#232C40] px-3 py-2">
                    <div className="min-w-0">
                      <div className="truncate text-slate-100">{fmtTime(ev.started_at)} · {ev.duration_min}m</div>
                      <div className="text-xs text-slate-400 capitalize">{ev.classification}</div>
                    </div>
                    {ev.flagged
                      ? <div className="text-right"><div className="text-xs font-medium text-red-400">{money(ev.expected_price)}</div><div className="text-[10px] text-amber-300/90">{ev.confidence ?? 94}% conf.</div></div>
                      : <span className="text-xs text-emerald-400">Matched</span>}
                  </div>
                ))}
                {data?.recent?.length === 0 && (
                  <div className="text-sm text-slate-400 py-4 text-center">No services in this window.</div>
                )}
              </div>
            </div>
          </div>

          {/* Chair management */}
          <div className="rounded-xl border border-[#2A3550]/60 bg-[#171E2E] p-4 space-y-4" data-testid="chair-settings">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4" style={{ color: branding.primary_color }} />
              <div className="text-sm font-medium">Chair settings</div>
            </div>

            <div className="flex items-center justify-between gap-3 rounded-lg border border-[#2A3550] bg-[#1E2638] px-4 py-3">
              <div>
                <div className="text-sm font-medium">{active ? "This chair is online" : "This chair is offline"}</div>
                <div className="text-xs text-slate-400">Take a broken or spare chair offline — its history stays intact and it's skipped in reconciliation.</div>
              </div>
              <button onClick={toggleActive} disabled={busy} data-testid="chair-toggle-active"
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium border transition-colors duration-200 disabled:opacity-50 ${active ? "border-amber-500/50 text-amber-300 hover:bg-amber-500/10" : "border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10"}`}>
                <Power className="h-4 w-4" /> {active ? "Take offline" : "Bring online"}
              </button>
            </div>

            <div className="rounded-lg border border-[#2A3550] bg-[#1E2638] px-4 py-3 space-y-3" data-testid="chair-charitable">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-start gap-2">
                  <Heart className="h-4 w-4 mt-0.5 text-rose-400" />
                  <div>
                    <div className="text-sm font-medium">Charitable chair</div>
                    <div className="text-xs text-slate-400">Free cuts here register as genuine services credited to a cause — never counted as leakage. Video is still kept to prevent abuse.</div>
                  </div>
                </div>
                <Switch checked={charitable} disabled={busy} data-testid="chair-charitable-toggle" onCheckedChange={toggleCharitable} />
              </div>
              {charitable && (
                <div className="space-y-2" data-testid="chair-beneficiary-block">
                  <label className="text-[11px] uppercase tracking-[0.15em] text-slate-400 font-semibold">Giving goes to</label>
                  <select value={benId} disabled={busy} data-testid="chair-beneficiary-select"
                    onChange={(e) => { setBenId(e.target.value); saveCharitable(true, e.target.value); }}
                    className="w-full rounded-lg bg-[#171E2E] border border-[#2A3550] px-3 py-2 text-sm text-slate-100 focus:border-[#38BDF8] focus:outline-none">
                    <option value="">Unassigned</option>
                    {(beneficiaries || []).map((b) => (
                      <option key={b.id} value={b.id}>{b.name}{b.scope === "network" ? " · network" : ""}</option>
                    ))}
                  </select>
                  <div className="flex items-center gap-2">
                    <input value={newBen} data-testid="chair-beneficiary-new"
                      onChange={(e) => setNewBen(e.target.value)}
                      placeholder="Add a cause (e.g. Haircuts for Veterans)"
                      className="flex-1 rounded-lg bg-[#171E2E] border border-[#2A3550] px-3 py-2 text-sm text-slate-100 focus:border-[#38BDF8] focus:outline-none" />
                    <button onClick={addBeneficiary} disabled={busy || !newBen.trim()} data-testid="chair-beneficiary-add"
                      className="rounded-lg px-3 py-2 text-sm font-medium text-white disabled:opacity-50" style={{ backgroundColor: "var(--brand)" }}>
                      Add
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold mb-2">Custom thresholds (min) — leave blank to use store default</div>
              <div className="grid grid-cols-3 gap-3">
                {[["stage1_min", "Stage 1", gthr?.stage1_min], ["sale_engage_min", "Sale engages", gthr?.sale_engage_min], ["expected_min", "Extended after", gthr?.expected_min]].map(([k, label, def]) => (
                  <div key={k}>
                    <label className="text-[11px] text-slate-400">{label}</label>
                    <input type="number" min={0} value={ov[k]} data-testid={`chair-thr-${k}`}
                      onChange={(e) => setOv((p) => ({ ...p, [k]: e.target.value }))}
                      placeholder={def != null ? `${def}` : ""}
                      className="mt-1 w-full rounded-lg bg-[#1E2638] border border-[#2A3550] px-3 py-2 text-sm text-slate-100 focus:border-[#38BDF8] focus:outline-none" />
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button onClick={saveThresholds} disabled={busy} data-testid="chair-save-thresholds"
                  className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50" style={{ backgroundColor: "var(--brand)" }}>
                  <Save className="h-4 w-4" /> Save thresholds
                </button>
                <button onClick={useDefaults} disabled={busy} data-testid="chair-use-defaults"
                  className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium border border-[#2A3550] bg-[#1E2638] text-slate-200 hover:border-[#38BDF8] transition-colors duration-200 disabled:opacity-50">
                  Use store default
                </button>
                <button onClick={removeChair} disabled={busy} data-testid="chair-remove"
                  className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors duration-200 disabled:opacity-50">
                  <Trash2 className="h-4 w-4" /> Remove chair
                </button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
